/**
 * A sound read as a score, without WebAudio: `ScoreKit` is a toolkit (sfx/kit.ts) whose voices are written down
 * (when, how long, how loud, at what pitch, which noise and filter) instead of scheduled. From that score:
 *
 * - `fingerprint`: a coarse time × frequency energy map (48 slices of 40 ms × 10 octave-ish bands), so the uniqueness
 *   test can tell two sounds apart (cosine similarity) and see that each one is not silent;
 * - `nodes`: the WebAudio nodes the real kit would make (the budget);
 * - `loudness`: the energy of its loudest 120 ms (to even out how loud the moves are, sfx/sounds/moves.ts).
 *
 * Plain logic, used by the moves' level trim at load and by the tests in Node.
 */
import { KitShared, SfxKit, type FmOpts, type NoiseOpts, type PluckOpts, type ToneOpts } from './kit';
import { mulberry32 } from '@nine/shared';
import { getSfx, type SfxCall } from './registry';

/** One voice as written: a band of energy that moves in pitch and fades. */
export interface ScoreVoice {
  at: number;
  dur: number;
  vol: number;
  attack: number;
  /** Centre frequency at the start, after `sweep` s, and at the end (Hz). */
  f0: number;
  f1: number;
  f2: number;
  sweep: number;
  /** How wide (in bands) the energy spreads around the centre: 0 a pure tone, 8 broadband noise. */
  spread: number;
  linear: boolean;
}

export const FP_SLICES = 48;
export const FP_SLICE_S = 0.04;
/** Band edges (Hz): 10 bands from 40 Hz to 16 kHz. */
const BAND_LO = 40;
const BAND_HI = 16000;
export const FP_BANDS = 10;

const bandOf = (f: number): number =>
  Math.max(0, Math.min(FP_BANDS - 1, (Math.log(Math.max(BAND_LO, f) / BAND_LO) / Math.log(BAND_HI / BAND_LO)) * FP_BANDS));

/** A stand-in for any WebAudio node or parameter: every method is a no-op that returns the stand-in again. */
function stub(): unknown {
  const fn = function () {
    return proxy;
  };
  const proxy: unknown = new Proxy(fn, {
    get: (_t, p) => (p === 'value' ? 1 : p === Symbol.toPrimitive ? () => 1 : proxy),
    set: () => true,
    apply: () => proxy,
  });
  return proxy;
}

/** Noise colours: centre and spread when unfiltered. */
const COLOR: Record<string, [number, number]> = { white: [6000, 9], pink: [1200, 8], brown: [180, 4], crackle: [3000, 8] };

export class ScoreKit extends SfxKit {
  readonly voices: ScoreVoice[] = [];
  scoreNodes = 0;

  constructor(seed = 7, call: SfxCall = { intensity: 1, len: undefined }, pitch = 1) {
    const ctx = stub() as BaseAudioContext;
    super(ctx, stub() as AudioNode, 0, pitch, mulberry32(seed >>> 0), null as unknown as KitShared, false, call);
  }

  private add(v: Omit<ScoreVoice, 'f1' | 'f2' | 'sweep'> & Partial<Pick<ScoreVoice, 'f1' | 'f2' | 'sweep'>>): void {
    const f1 = v.f1 ?? v.f0;
    this.voices.push({ ...v, f1, f2: v.f2 ?? f1, sweep: v.sweep ?? v.dur });
  }

  override gain(): GainNode {
    this.scoreNodes++;
    return stub() as GainNode;
  }
  override filter(): BiquadFilterNode {
    this.scoreNodes++;
    return stub() as BiquadFilterNode;
  }
  override pan(): StereoPannerNode {
    this.scoreNodes++;
    return stub() as StereoPannerNode;
  }
  override shaper(): WaveShaperNode {
    this.scoreNodes++;
    return stub() as WaveShaperNode;
  }
  override echo(): GainNode {
    this.scoreNodes += 4;
    return stub() as GainNode;
  }
  override lfo(): void {
    this.scoreNodes += 2;
  }

  override tone(o: ToneOpts): OscillatorNode {
    const rich = o.type === 'sawtooth' || o.type === 'square' ? 2 : o.type === 'triangle' ? 1 : 0;
    this.add({
      at: o.at ?? 0,
      dur: o.dur,
      vol: o.vol,
      attack: o.attack ?? 0.004,
      f0: this.f(o.f),
      f1: o.f1 !== undefined ? this.f(o.f1) : undefined,
      sweep: o.sweep,
      spread: rich,
      linear: !!o.linear,
    });
    this.scoreNodes += 2 + (o.vib ? 2 : 0);
    return stub() as OscillatorNode;
  }

  override noise(o: NoiseOpts): AudioBufferSourceNode {
    const [cf, cs] = COLOR[o.color ?? 'white']!;
    let f0 = cf;
    let spread = cs;
    if (o.f !== undefined) {
      f0 = this.f(o.f);
      const t = o.type ?? 'bandpass';
      // A wide filter spreads; a narrow band-pass keeps the energy close to its centre.
      spread = t === 'bandpass' ? Math.max(0.5, 3 / Math.max(0.5, o.q ?? 1)) : t === 'highpass' ? 4 : t === 'lowpass' ? 4 : 2;
      if (t === 'highpass') f0 = Math.min(BAND_HI, f0 * 2);
      if (t === 'lowpass') f0 = Math.max(BAND_LO, f0 / 2);
    }
    this.add({
      at: o.at ?? 0,
      dur: o.dur,
      vol: o.vol * (o.color === 'crackle' ? 0.35 : 1),
      attack: o.attack ?? 0.004,
      f0,
      f1: o.f1 !== undefined ? this.f(o.f1) : undefined,
      f2: o.f2 !== undefined ? this.f(o.f2) : undefined,
      sweep: o.sweep,
      spread,
      linear: !!o.linear,
    });
    this.scoreNodes += 3 + (o.f !== undefined ? 1 : 0) + (o.trem ? 3 : 0);
    return stub() as AudioBufferSourceNode;
  }

  override fm(o: FmOpts): OscillatorNode {
    this.add({
      at: o.at ?? 0,
      dur: o.dur,
      vol: o.vol,
      attack: o.attack ?? 0.003,
      f0: this.f(o.f),
      f1: o.f1 !== undefined ? this.f(o.f1) : undefined,
      spread: Math.min(4, 1 + o.index * 0.5),
      linear: false,
    });
    this.scoreNodes += 4;
    return stub() as OscillatorNode;
  }

  override pluck(o: PluckOpts): void {
    this.add({
      at: o.at ?? 0,
      dur: o.dur ?? 1.2,
      vol: o.vol * 0.6,
      attack: 0.002,
      f0: this.f(o.f),
      spread: 1 + (o.bright ?? 0.6),
      linear: true,
    });
    this.scoreNodes += 2;
  }
}


/** A voice's level at time `t` (s): its attack, then a fall to silence (exponential unless linear). */
function levelAt(v: ScoreVoice, t: number): number {
  const u = t - v.at;
  if (u < 0 || u > v.dur) return 0;
  if (u < v.attack) return (v.vol * u) / Math.max(1e-4, v.attack);
  const rest = Math.max(1e-4, v.dur - v.attack);
  const x = (u - v.attack) / rest;
  return v.linear ? v.vol * (1 - x) : v.vol * Math.pow(1e-4, x);
}

function freqAt(v: ScoreVoice, t: number): number {
  const u = Math.max(0, t - v.at);
  if (u <= v.sweep) return v.f0 * Math.pow(v.f1 / v.f0, u / Math.max(1e-4, v.sweep));
  const rest = Math.max(1e-4, v.dur - v.sweep);
  return v.f1 * Math.pow(v.f2 / v.f1, Math.min(1, (u - v.sweep) / rest));
}

/** The time × band energy map of a score, flattened (slice-major). */
export function fingerprintOf(voices: readonly ScoreVoice[]): Float64Array {
  const out = new Float64Array(FP_SLICES * FP_BANDS);
  const SUB = 4;
  for (const v of voices)
    for (let s = 0; s < FP_SLICES; s++) {
      const t0 = s * FP_SLICE_S;
      if (t0 > v.at + v.dur || t0 + FP_SLICE_S < v.at) continue;
      for (let q = 0; q < SUB; q++) {
        const t = t0 + ((q + 0.5) / SUB) * FP_SLICE_S;
        const a = levelAt(v, t);
        if (a <= 0) continue;
        const e = (a * a) / SUB;
        const c = bandOf(freqAt(v, t));
        // Spread the energy over neighbouring bands (a triangle `spread` bands wide each side).
        const w = Math.max(0.35, v.spread);
        let norm = 0;
        for (let b = 0; b < FP_BANDS; b++) norm += Math.max(0, 1 - Math.abs(b - c) / w);
        for (let b = 0; b < FP_BANDS; b++) {
          const k = Math.max(0, 1 - Math.abs(b - c) / w);
          if (k > 0) out[s * FP_BANDS + b]! += (e * k) / norm;
        }
      }
    }
  return out;
}

/** Cosine similarity of two fingerprints (compared on the square root of the energy: closer to how loud it seems). */
export function similarity(a: Float64Array, b: Float64Array): number {
  let ab = 0;
  let aa = 0;
  let bb = 0;
  for (let i = 0; i < a.length; i++) {
    const x = Math.sqrt(a[i]!);
    const y = Math.sqrt(b[i]!);
    ab += x * y;
    aa += x * x;
    bb += y * y;
  }
  return aa && bb ? ab / Math.sqrt(aa * bb) : 0;
}

/** The energy of the loudest 120 ms (3 slices) of a fingerprint. */
export function loudnessOf(fp: Float64Array): number {
  const per = new Float64Array(FP_SLICES);
  for (let s = 0; s < FP_SLICES; s++) for (let b = 0; b < FP_BANDS; b++) per[s]! += fp[s * FP_BANDS + b]!;
  let best = 0;
  for (let s = 0; s + 2 < FP_SLICES; s++) best = Math.max(best, (per[s]! + per[s + 1]! + per[s + 2]!) / 3);
  return best;
}

export interface Score {
  voices: ScoreVoice[];
  nodes: number;
  fp: Float64Array;
  loudness: number;
  /** When the last voice ends (s). */
  length: number;
}

/** A registered sound written down (a fixed seed, intensity 1). */
export function scoreOf(id: string, seed = 7, call: Partial<SfxCall> = {}): Score {
  const def = getSfx(id);
  if (!def) throw new Error(`no sound '${id}'`);
  return scoreFn((k, o) => def.play(k, o), seed, call, def.pitch ?? 1);
}

/** Any sound function written down. */
export function scoreFn(
  play: (k: SfxKit, o: SfxCall) => void,
  seed = 7,
  call: Partial<SfxCall> = {},
  pitch = 1,
): Score {
  const o: SfxCall = { intensity: call.intensity ?? 1, len: call.len };
  const k = new ScoreKit(seed, o, pitch);
  play(k, o);
  const fp = fingerprintOf(k.voices);
  return {
    voices: k.voices,
    nodes: k.scoreNodes,
    fp,
    loudness: loudnessOf(fp),
    length: k.voices.reduce((m, v) => Math.max(m, v.at + v.dur), 0),
  };
}

/** Only the loudness (the loudest 120 ms of energy), cheap enough to run for every move at load. */
export function quickLoudness(voices: readonly ScoreVoice[]): number {
  const per = new Float64Array(FP_SLICES);
  for (const v of voices)
    for (let s = 0; s < FP_SLICES; s++) {
      const t0 = s * FP_SLICE_S;
      if (t0 > v.at + v.dur || t0 + FP_SLICE_S < v.at) continue;
      for (let q = 0; q < 2; q++) {
        const a = levelAt(v, t0 + (q + 0.5) * FP_SLICE_S * 0.5);
        per[s]! += (a * a) / 2;
      }
    }
  let best = 0;
  for (let s = 0; s + 2 < FP_SLICES; s++) best = Math.max(best, (per[s]! + per[s + 1]! + per[s + 2]!) / 3);
  return best;
}
